import * as path from "node:path";

import { EXIT_NEEDS_AGENT, EXIT_OK, type CommandOutput } from "../../common.js";
import { atomicWriteJson, assertScriptUnchanged } from "../../infra/script-working-copy.js";
import { PRODUCTION_LLM_COMPOSITION, type LlmComposition } from "../../llm/composition.js";
import { assetAuditIssueLine, auditScriptAssets } from "../ingest/final-asset-audit.js";
import { loadScriptForEdit } from "./session.js";
import { type Dict, strOf } from "../../domain/script/shared.js";

export async function commandAuditAssets(
  opts: Dict,
  composition: LlmComposition = PRODUCTION_LLM_COMPOSITION,
): Promise<[CommandOutput, number]> {
  const session = await loadScriptForEdit(opts);
  const executor = composition.createExecutor(strOf(opts["llm_config"]).trim() || undefined);
  const audit = await auditScriptAssets(session.script, executor);

  // A model call can take long enough for another process to edit script.json.
  // Never attach the old audit to a newer script revision.
  if (session.scriptPath) assertScriptUnchanged(session.scriptPath, session.baselineHash);
  const baseDir = session.scriptPath ? path.dirname(session.scriptPath) : path.resolve(session.workspace);
  const outputPath = path.resolve(strOf(opts["output"]).trim() || path.join(baseDir, "asset-audit.json"));
  atomicWriteJson(outputPath, audit);

  const hasIssues = audit.issues.length > 0;
  const issueLines = audit.issues.map(assetAuditIssueLine);
  const summary = `${audit.summary.issues} issues; confidence high=${audit.summary.high} medium=${audit.summary.medium} low=${audit.summary.low}`;
  const hint = "Inspect each finding, repair confirmed duplicates, then rerun `scriptctl audit-assets` and `scriptctl validate`.";
  const report: CommandOutput = {
    text: [summary, ...issueLines, `output: ${outputPath}`, ...(hasIssues ? [hint] : [])].join("\n"),
    data: {
      op: "script.audit-assets",
      changed: false,
      issues: audit.issues,
      output: outputPath,
      asset_audit: audit,
      config_source: executor.configSource,
      llm_routes: executor.usage(),
    },
  };
  return [report, hasIssues ? EXIT_NEEDS_AGENT : EXIT_OK];
}
